import React, { useEffect, useState, useRef } from 'react';
import _ from 'lodash';
import classnames from 'classnames';

import { browserHistory } from 'react-router';
import { useSelector } from 'react-redux';

import makeStyles from '@mui/styles/makeStyles';
import FormLabel from '@mui/material/FormLabel';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Popover from '@mui/material/Popover';
import Button from '@mui/material/Button';

import flags from 'containers/flags/service';
import PopUp from 'widgets-v5/pop-up';
import AdSummary from 'containers/ad-summary';
import FillProgressBar from 'widgets-v5/fill-progress-bar';
import StatsSummaryTable from './modules/stats-summary-table';
import ElapsedTimeProgressBar from 'widgets-v5/elapsed-time-progress-bar';
import { TemporaryDrawer } from 'widgets-v6/modal';

import CampaignOverallProgress from './modules/campaign-overall-progress';
import Separator from 'widgets-v5/separator';
import { NeutralButton } from 'widgets-v5/buttons';
import TimezoneSelector from 'widgets-v5/timezone-selector';
import { SummaryContent } from 'pages/campaigns/campaigns-overview/modules/campaign-summary/summary-content';
import {
    formatTimeRangeInDaysHours,
    formatNumber_percentage,
    formatNumber_whole,
    formatNumber_currency,
} from 'utils/formatting';
import { getDateProgressRelativeToRange } from 'common/date-calculator';
import {
    createGoalDeliveryString,
    createRemainingDeliveryString,
    createBookedDeliveryString,
} from './modules/campaign-overall-progress';

const useStyles = makeStyles(theme => ({
    toolbar: {
        display: 'flex',
        justifyContent: 'flex-end',
    },
    drawer: {
        minWidth: '45%',
        maxWidth: '60%',
        width: '55%',
    },
    drawerContent: {
        display: 'flex',
        flexDirection: 'column',
        maxHeight: 'calc(100% - 64px)',
        margin: theme.spacing(2),
    },
    drawerHeader: {
        flex: '0 0 auto',
    },
    drawerBody: {
        flex: '1 1 auto',
        overflowY: 'auto',
        overflowX: 'hidden',
    },
    metrics: {
        padding: '0px 35px',
    },
    metric: {
        fontSize: 14,
    },
    infoIcon: {
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        paddingTop: '15px',
        paddingLeft: '40%',
    },
    popoverContent: {
        padding: theme.spacing(2),
        minWidth: '320px',
        maxHeight: '600px',
    },
}));

const GOAL_MAPPING = {
    SPEND: 'spend',
    IMPRESSIONS: 'impressions',
    BILLINGS: 'billings',
};

export function getPacingColor(pacingNumber, isEnded) {
    const pacingPercentage = pacingNumber * 100;
    let pacingColor;
    if (isEnded) {
        if (pacingPercentage >= 100) {
            pacingColor = '#6BDF7E';
        } else {
            pacingColor = '#ffbfcc';
        }
    } else {
        if (pacingPercentage >= 90) {
            pacingColor = '#6BDF7E';
        } else if (pacingPercentage >= 80) {
            pacingColor = '#FFCE51';
        } else if (pacingPercentage < 80) {
            pacingColor = '#ffbfcc';
        }
    }
    return pacingColor;
}

export function getProjectedPacingColor(projectedPacingNumber) {
    const projectedPacingPercentage = projectedPacingNumber * 100;
    let projectedPacingColor;
    if (projectedPacingPercentage === 100) {
        projectedPacingColor = '#6BDF7E';
    } else {
        projectedPacingColor = '#ffbfcc';
    }
    return projectedPacingColor;
}

export function getTargetFormat(isCurrencyFormat, value) {
    const isNegative = value < 0;
    const targetFormat = isCurrencyFormat
        ? formatNumber_currency(value)
        : formatNumber_whole(value);
    return isNegative ? targetFormat.replace('$-', '-$') : targetFormat;
}

export function ProgressLayout(props) {
    // If adId or currentAd is present, render ad-level (ProgressLayout), else campaign-level (GraphLayout)
    const classes = useStyles();
    const { viewBy } = useSelector(state => state.campaignPageContainer || {});
    const [open, setOpen] = useState(false);
    const {
        campaignId,
        campaign,
        statsTableData,
        summary,
        ads,
        workspace,
        flight,
        dataForOverallGraph,
        organizationTimezone,
        timezoneAtCampaignLevel,
        changeTimezoneAtCampaignLevel,
        isLoading,
        progressData,
        campaignMetadata,
        routes,
    } = props;
    const { adId } = props.params || {};
    const currentAd = ads && adId ? _.find(ads, ad => parseInt(adId) === ad.id) : null;

    useEffect(() => {
        if (adId && currentAd) {
            changeTimezoneAtCampaignLevel(currentAd.timezone);
        } else if (campaignId && !adId) {
            changeTimezoneAtCampaignLevel(campaign.default_timezone);
        }
    }, [routes]);
    // --- Drawer logic ---
    const openDrawer = () => setOpen(true);
    const closeDrawer = () => {
        setOpen(false);
        browserHistory.push(`/campaigns/${campaignId}/progress/`);
    };
    // --- Metrics logic ---
    let metricsLeftColumn = [];
    let metricsMiddleColumn = [];
    let metricsRightColumn = [];
    let isEnded,
        isAutoCampaign,
        isCurrencyFormat,
        totalPacingColor,
        projectedPacingColor,
        goalTitle,
        isCampaignGraphVisible;
    if (currentAd && progressData) {
        // Ad-level
        const {
            currentDelivered = 0,
            currentPacing = 0,
            goal = '',
            idealDelivered = 0,
            projectedDelivery = 0,
            totalTarget = 0,
            revenueAtRisk = 0,
            goalAtRisk = 0,
            projectedPacing = 0,
            variance = 0,
        } = progressData;
        projectedPacingColor = getProjectedPacingColor(projectedPacing);
        isEnded = currentAd.status === 'ended';
        totalPacingColor = getPacingColor(currentPacing, isEnded);
        isAutoCampaign = campaign.budget_allocation_method === 'auto';
        isCurrencyFormat =
            _.capitalize(goal) === 'Billings' ||
            _.capitalize(goal) === 'Spend' ||
            _.capitalize(goal) === 'Revenue';
        goalTitle = _.capitalize(goal === 'billings' ? 'revenue' : goal);
        if (isEnded) {
            metricsLeftColumn = [
                {
                    title: `Delivered ${goalTitle}`,
                    value: getTargetFormat(isCurrencyFormat, currentDelivered),
                    color: 'black',
                },
                {
                    title: 'Target',
                    value: getTargetFormat(isCurrencyFormat, totalTarget),
                    color: 'black',
                },
            ];
            metricsMiddleColumn = [
                {
                    title: 'Delivered %',
                    value: formatNumber_percentage(currentPacing),
                    color: totalPacingColor,
                },
                {
                    title: 'Variance',
                    value: getTargetFormat(isCurrencyFormat, variance),
                    color: 'black',
                },
            ];
        } else {
            metricsLeftColumn = [
                {
                    title: 'Current Pacing',
                    value: formatNumber_percentage(currentPacing),
                    color: totalPacingColor,
                },
                {
                    title: `Delivered ${goalTitle}`,
                    value: getTargetFormat(isCurrencyFormat, currentDelivered),
                    color: 'black',
                },
                {
                    title: 'Ideal Delivery',
                    value: getTargetFormat(isCurrencyFormat, idealDelivered),
                    color: 'black',
                },
            ];
            metricsMiddleColumn = [
                {
                    title: 'Projected Pacing',
                    value: currentPacing === 0 ? 'N/A' : formatNumber_percentage(projectedPacing),
                    color: projectedPacingColor,
                },
                {
                    title: 'Projected Delivery',
                    value:
                        currentPacing === 0
                            ? 'N/A'
                            : getTargetFormat(isCurrencyFormat, projectedDelivery),
                    color: 'black',
                },
                {
                    title: 'Target',
                    value: getTargetFormat(isCurrencyFormat, totalTarget),
                    color: 'black',
                },
            ];
            if (progressData.goal !== 'billings') {
                metricsRightColumn.push({
                    title: `${goalTitle} At Risk`,
                    value:
                        progressData.goal === 'spend'
                            ? formatNumber_currency(goalAtRisk)
                            : formatNumber_whole(goalAtRisk),
                    color: 'black',
                });
            }
            if (campaign.revenueModel !== 'disabled') {
                metricsRightColumn.push({
                    title: 'Revenue At Risk',
                    value: formatNumber_currency(revenueAtRisk),
                    color: 'black',
                });
            }
        }
        isCampaignGraphVisible = false; // Ad-level never shows campaign graph
    } else if (campaignMetadata) {
        // Campaign-level
        const {
            currentPacing,
            currentDelivered,
            goal,
            idealDelivered,
            totalTarget,
            projectedPacing,
            projectedDelivery,
            spendAtRisk,
            revenueAtRisk,
            variance,
        } = campaignMetadata;
        projectedPacingColor = getProjectedPacingColor(projectedPacing);
        isEnded = campaign.status === 'ended';
        totalPacingColor = getPacingColor(currentPacing, isEnded);
        if (goal === GOAL_MAPPING.SPEND) {
            goalTitle = 'Spend';
        } else if (goal === GOAL_MAPPING.IMPRESSIONS) {
            goalTitle = 'Impressions';
        } else {
            goalTitle = 'Revenue';
        }
        const showFormattedNumber = (goalType, value) => {
            return goalType === GOAL_MAPPING.IMPRESSIONS
                ? formatNumber_whole(value)
                : formatNumber_currency(value);
        };
        if (isEnded) {
            metricsLeftColumn = [
                {
                    title: `Delivered ${goalTitle}`,
                    value: showFormattedNumber(goal, currentDelivered),
                    color: 'black',
                },
                {
                    title: 'Target',
                    value: showFormattedNumber(goal, totalTarget),
                    color: 'black',
                },
            ];
            metricsMiddleColumn = [
                {
                    title: 'Delivered %',
                    value: formatNumber_percentage(currentPacing),
                    color: totalPacingColor,
                },
                {
                    title: 'Variance',
                    value: formatNumber_currency(variance),
                    color: 'black',
                },
            ];
        } else {
            metricsLeftColumn = [
                {
                    title: 'Current Pacing',
                    value: formatNumber_percentage(currentPacing),
                    color: totalPacingColor,
                },
                {
                    title: `Delivered ${goalTitle}`,
                    value: showFormattedNumber(goal, currentDelivered),
                    color: 'black',
                },
                {
                    title: 'Ideal Delivery',
                    value: showFormattedNumber(goal, idealDelivered),
                    color: 'black',
                },
            ];
            let projectedDeliveryValue = '';
            if (currentPacing === 0) {
                projectedDeliveryValue = 'N/A';
            } else {
                projectedDeliveryValue = showFormattedNumber(goal, projectedDelivery);
            }
            metricsMiddleColumn = [
                {
                    title: 'Projected Pacing',
                    value: currentPacing === 0 ? 'N/A' : formatNumber_percentage(projectedPacing),
                    color: projectedPacingColor,
                },
                {
                    title: 'Projected Delivery',
                    value: projectedDeliveryValue,
                    color: 'black',
                },
                {
                    title: 'Target',
                    value: showFormattedNumber(goal, totalTarget),
                    color: 'black',
                },
            ];
            if (goal === GOAL_MAPPING.SPEND) {
                metricsRightColumn.push({
                    title: 'Spend At Risk',
                    value: formatNumber_currency(spendAtRisk),
                    color: 'black',
                });
            } else if (goal === GOAL_MAPPING.BILLINGS) {
                metricsRightColumn.push({
                    title: 'Revenue At Risk',
                    value: formatNumber_currency(revenueAtRisk),
                    color: 'black',
                });
            } else if (goal === GOAL_MAPPING.IMPRESSIONS) {
                metricsRightColumn.push({
                    title: 'Impressions At Risk',
                    value: formatNumber_whole(spendAtRisk),
                    color: 'black',
                });
            }
        }
        isAutoCampaign = campaign.budget_allocation_method === 'auto';
        const isViewingByEntireCampaign =
            viewBy === 'campaign' && campaign.flightPacingStrategy === 'campaign';
        isCampaignGraphVisible = isAutoCampaign && !isViewingByEntireCampaign;
    }
    // --- Render ---
    return (
        <div className="ef5-progress-page">
            <Box mb={2}>
                <Grid container alignItems="center" spacing={2}>
                    <Grid item sm={12} md={8} lg={9}>
                        <i className="fa fa-info-circle ef5-progress-page__filter-info" />
                        {
                            'Real-time data may contain unvalidated impressions and costs that will be removed within three days.'
                        }
                    </Grid>
                    <Grid item sm={12} md={4} lg={3}>
                        <Box mb={1}>
                            <FormLabel>Timezone</FormLabel>
                        </Box>
                        <TimezoneSelector
                            timezone={timezoneAtCampaignLevel}
                            existingTimezones={[organizationTimezone, campaign.default_timezone]}
                            onChange={changeTimezoneAtCampaignLevel}
                        />
                    </Grid>
                </Grid>
            </Box>
            <div
                className={classnames('ef5-progress-page__workspace', {
                    'ef5-progress-page__workspace_is-campaign-progress': !workspace,
                })}
            >
                <div className="ef5-progress-page__ad-list">
                    <CampaignOverallProgress
                        ads={ads}
                        currentAd={adId}
                        campaign={campaign}
                        dataForOverallGraph={dataForOverallGraph}
                        campaignId={campaignId}
                        flight={flight}
                        campaignMetadata={campaignMetadata}
                        timezoneAtCampaignLevel={timezoneAtCampaignLevel}
                        onClick={openDrawer}
                        isLoading={isLoading}
                    />
                </div>
                <TemporaryDrawer
                    side="right"
                    open={open}
                    onClose={closeDrawer}
                    classes={{ paper: classes.drawer }}
                >
                    <div className={classes.drawerContent}>
                        {(currentAd || campaignId) && (
                            <div className={classes.drawerHeader}>
                                <Grid
                                    container
                                    justifyContent="space-between"
                                    alignItems="center"
                                    wrap="nowrap"
                                >
                                    <Grid item>
                                        <h1 className="ef5-progress-page__ad-name">
                                            #{currentAd ? currentAd.id : campaignId}{' '}
                                            {currentAd ? currentAd.name : campaign.name}
                                        </h1>
                                    </Grid>
                                    <Grid item>
                                        <IconButton
                                            aria-label="close"
                                            onClick={closeDrawer}
                                            size="large"
                                        >
                                            <CloseIcon />
                                        </IconButton>
                                    </Grid>
                                </Grid>
                            </div>
                        )}
                        <Box mb={2}>
                            <Grid
                                container
                                alignItems="center"
                                justifyContent="flex-end"
                                spacing={2}
                            >
                                <Grid item sm={12} md={8} lg={5}>
                                    <Box mb={1}>
                                        <FormLabel>
                                            {currentAd ? 'Ad Timezone' : 'Campaign Timezone'}
                                        </FormLabel>
                                    </Box>
                                    <TimezoneSelector
                                        timezone={timezoneAtCampaignLevel}
                                        existingTimezones={[
                                            organizationTimezone,
                                            campaign.default_timezone,
                                            ...(campaign.adTimezones || []),
                                            'UTC',
                                        ]}
                                        onChange={changeTimezoneAtCampaignLevel}
                                    />
                                </Grid>
                            </Grid>
                        </Box>
                        {currentAd && (
                            <div className={classes.drawerBody}>
                                <Separator />
                                <AdProgressRow
                                    campaignStart={flight.start}
                                    campaignEnd={flight.end}
                                    currentAd={currentAd}
                                    campaign={campaign}
                                    isLoading={isLoading}
                                />
                                {workspace ? workspace : <CircularProgress />}
                                {!isAutoCampaign && (
                                    <Grid container className={classes.metrics} spacing={4}>
                                        <Grid item xs={isEnded ? 6 : 4}>
                                            {_.map(metricsLeftColumn, metric => (
                                                <Box
                                                    display="flex"
                                                    justifyContent="space-between"
                                                    key={metric.title}
                                                    color={metric.color}
                                                >
                                                    <Typography
                                                        className={classes.metric}
                                                        color="textSecondary"
                                                    >
                                                        {`${metric.title}:`}
                                                    </Typography>
                                                    <Typography className={classes.metric}>
                                                        {metric.value}
                                                    </Typography>
                                                </Box>
                                            ))}
                                        </Grid>
                                        <Grid item xs={isEnded ? 6 : 4}>
                                            {_.map(metricsMiddleColumn, metric => (
                                                <Box
                                                    display="flex"
                                                    justifyContent="space-between"
                                                    key={metric.title}
                                                    color={metric.color}
                                                >
                                                    <Typography
                                                        className={classes.metric}
                                                        color="textSecondary"
                                                    >{`${metric.title}:`}</Typography>
                                                    <Typography className={classes.metric}>
                                                        {metric.value}
                                                    </Typography>
                                                </Box>
                                            ))}
                                        </Grid>
                                        {!isEnded && (
                                            <Grid item xs={4}>
                                                {_.map(metricsRightColumn, metric => (
                                                    <Box
                                                        display="flex"
                                                        key={metric.title}
                                                        justifyContent="space-between"
                                                        color={metric.color}
                                                    >
                                                        <Typography
                                                            className={classes.metric}
                                                            color="textSecondary"
                                                        >{`${metric.title}:`}</Typography>
                                                        <Typography className={classes.metric}>
                                                            {metric.value}
                                                        </Typography>
                                                    </Box>
                                                ))}
                                            </Grid>
                                        )}
                                    </Grid>
                                )}
                                {!isAutoCampaign && (
                                    <Grid
                                        container
                                        direction="row"
                                        justifyContent="center"
                                        alignItems="center"
                                    >
                                        <Grid item xs={12}>
                                            <Typography className={classes.infoIcon}>
                                                <InfoOutlinedIcon fontSize="small" />
                                                <Typography>
                                                    Progress Graph updated every 1 hour
                                                </Typography>
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                )}
                                <StatsTable
                                    ad={currentAd}
                                    campaignId={campaignId}
                                    status={currentAd.status}
                                    summary={summary}
                                    statsTableData={statsTableData}
                                    changeTimezone={changeTimezoneAtCampaignLevel}
                                    isLoading={isLoading}
                                />
                            </div>
                        )}
                        {!currentAd && isCampaignGraphVisible && (
                            <div className={classes.drawerBody}>
                                <Separator />
                                {workspace ? workspace : <CircularProgress />}
                            </div>
                        )}
                    </div>
                    {!currentAd && isCampaignGraphVisible && (
                        <Grid container className={classes.metrics} spacing={4}>
                            <Grid item xs={isEnded ? 6 : 4}>
                                {_.map(metricsLeftColumn, metric => (
                                    <Box
                                        display="flex"
                                        justifyContent="space-between"
                                        key={metric.title}
                                        color={metric.color}
                                    >
                                        <Typography
                                            className={classes.metric}
                                            color="textSecondary"
                                        >
                                            {`${metric.title}:`}
                                        </Typography>
                                        <Typography className={classes.metric}>
                                            {metric.value}
                                        </Typography>
                                    </Box>
                                ))}
                            </Grid>
                            <Grid item xs={isEnded ? 6 : 4}>
                                {_.map(metricsMiddleColumn, metric => (
                                    <Box
                                        display="flex"
                                        justifyContent="space-between"
                                        key={metric.title}
                                        color={metric.color}
                                    >
                                        <Typography
                                            className={classes.metric}
                                            color="textSecondary"
                                        >{`${metric.title}:`}</Typography>
                                        <Typography className={classes.metric}>
                                            {metric.value}
                                        </Typography>
                                    </Box>
                                ))}
                            </Grid>
                            {!isEnded && (
                                <Grid item xs={4}>
                                    {_.map(metricsRightColumn, metric => (
                                        <Box
                                            display="flex"
                                            key={metric.title}
                                            justifyContent="space-between"
                                            color={metric.color}
                                        >
                                            <Typography
                                                className={classes.metric}
                                                color="textSecondary"
                                            >{`${metric.title}:`}</Typography>
                                            <Typography className={classes.metric}>
                                                {metric.value}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Grid>
                            )}
                        </Grid>
                    )}
                    {!currentAd && isCampaignGraphVisible && (
                        <Grid container direction="row" justifyContent="center" alignItems="center">
                            <Grid item xs={12}>
                                <Typography className={classes.infoIcon}>
                                    <InfoOutlinedIcon fontSize="small" />
                                    <Typography>Progress Graph updated every 1 hour</Typography>
                                </Typography>
                            </Grid>
                        </Grid>
                    )}
                    {!currentAd && (
                        <StatsTable
                            campaignId={campaignId}
                            status={campaign.status}
                            summary={summary}
                            statsTableData={statsTableData}
                            changeTimezone={changeTimezoneAtCampaignLevel}
                            isLoading={false}
                        />
                    )}
                </TemporaryDrawer>
            </div>
        </div>
    );
}

function AdProgressRow(props) {
    const { campaignStart, campaignEnd, campaign } = props;
    const ad = props.currentAd;

    const { goal, totalTarget, totalDelivered, dailyDelivered, dailyCap, maxTotalDelivery } = ad;

    const elapsedTime = {
        start:
            getDateProgressRelativeToRange({ start: campaignStart, end: campaignEnd }, ad.start) *
            100,
        end:
            100 -
            getDateProgressRelativeToRange({ start: campaignStart, end: campaignEnd }, ad.end) *
                100,
    };

    if (campaign.budget_allocation_method === 'auto') {
        return null;
    }

    return (
        <div className="ef3-campaign-delivery__progress-section ef3-campaign-delivery__ad-progress-row">
            {campaign.budget_allocation_method === 'manual' && (
                <div className="ef3-campaign-delivery__elapsedBar">
                    <ElapsedTimeProgressBar
                        type="ad-row"
                        deliveryHealth={ad.health}
                        deliveryProgress={(totalDelivered / maxTotalDelivery) * 100}
                        status={ad.status}
                        campaignFlight={{ campaignStart, campaignEnd }}
                        startDate={new Date(ad.start)}
                        endDate={new Date(ad.end)}
                        offsetLeft={elapsedTime.start}
                        offsetRight={elapsedTime.end}
                        labelLeft={`${formatTimeRangeInDaysHours({
                            start: ad.start,
                            end: ad.end,
                        })} booked`}
                    />
                </div>
            )}
            <div className="ef3-campaign-delivery__stats">
                {totalTarget !== 0 ? (
                    <span>
                        <p>{createGoalDeliveryString(totalDelivered, goal)}</p>
                        {campaign.budget_allocation_method === 'manual' && (
                            <p>
                                {createRemainingDeliveryString(totalTarget, totalDelivered, goal)}{' '}
                                remaining / {createBookedDeliveryString(totalTarget, goal)}
                            </p>
                        )}
                    </span>
                ) : null}
                {dailyCap ? (
                    <span>
                        <p>{createBookedDeliveryString(dailyCap, goal)} today</p>
                        <p>
                            {createGoalDeliveryString(dailyDelivered, goal)} today /{' '}
                            {createRemainingDeliveryString(dailyCap, dailyDelivered, goal)}{' '}
                            remaining today
                        </p>
                    </span>
                ) : null}
            </div>
            <div className="ef3-campaign-delivery__fill-progress">
                {totalTarget ? (
                    <FillProgressBar type={goal} goal={totalTarget} current={totalDelivered} />
                ) : null}
            </div>
        </div>
    );
}

function StatsTable(props) {
    const classes = useStyles();
    const [anchorEl, setAnchorEl] = useState(null);

    const { statsTableData, ad, status, campaignId, isLoading } = props;

    const handleClick = event => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const open = Boolean(anchorEl);

    const id = open ? 'simple-popover' : undefined;

    const popoverRef = useRef();

    // Feature flag for summary popover
    if (flags.isEnabled && flags.isEnabled('en_2109_ad_summary_fix_height')) {
        return (
            <div className="ef3-campaign-delivery__stats-table">
                <div className={classes.toolbar}>
                    <Button
                        aria-describedby={id}
                        variant="outlined"
                        color="primary"
                        onClick={handleClick}
                    >
                        Summary
                    </Button>
                    <Popover
                        id={id}
                        open={open}
                        anchorEl={anchorEl}
                        onClose={handleClose}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'center',
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'center',
                        }}
                        action={popoverRef}
                    >
                        <Box m={2} minWidth={300} width={500}>
                            {ad ? (
                                <AdSummary
                                    adId={ad.id}
                                    campaignId={campaignId}
                                    onDoneLoading={() =>
                                        popoverRef.current && popoverRef.current.updatePosition()
                                    }
                                />
                            ) : (
                                <SummaryContent
                                    campaignId={campaignId}
                                    subTitle="Campaign Summary"
                                />
                            )}
                        </Box>
                    </Popover>
                </div>
                <StatsSummaryTable
                    className="ef3-campaign-delivery__table-body"
                    headerItems={statsTableData.headerItems}
                    bodyItems={statsTableData.bodyItems}
                    isLiveStats={status === 'live'}
                    isLoading={isLoading}
                />
            </div>
        );
    }
    // Default summary pop-up
    return (
        <div className="ef3-campaign-delivery__stats-table">
            <div className={classes.toolbar}>
                {ad ? (
                    <PopUp
                        className="ef3-campaign-delivery__summary-popup"
                        position="left"
                        contents={<AdSummary adId={ad.id} campaignId={campaignId} />}
                    >
                        <NeutralButton icon={<i className="fa fa-list" />} text="Summary" />
                    </PopUp>
                ) : (
                    <React.Fragment>
                        <NeutralButton
                            onClick={handleClick}
                            icon={<i className="fa fa-list" />}
                            text="Summary"
                        />
                        <Popover
                            id="campaign-summary-popup"
                            open={open}
                            anchorEl={anchorEl}
                            onClose={handleClose}
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'left',
                            }}
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'right',
                            }}
                        >
                            <div className={classes.popoverContent}>
                                <SummaryContent
                                    campaignId={campaignId}
                                    subTitle="Campaign Summary"
                                />
                            </div>
                        </Popover>
                    </React.Fragment>
                )}
            </div>
            <StatsSummaryTable
                className="ef3-campaign-delivery__table-body"
                headerItems={statsTableData.headerItems}
                bodyItems={statsTableData.bodyItems}
                isLiveStats={status === 'live'}
                isLoading={isLoading}
            />
        </div>
    );
}

export default ProgressLayout;
